import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingCart, User, Menu, Globe } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "./Logo";
import { useT, useSite } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { useAuth, isStaff } from "@/lib/auth";

export const NAV = [
  ["home", "/"], ["about", "/nosotros"], ["services", "/servicios"], ["solutions", "/soluciones"],
  ["clients", "/clientes"], ["resources", "/recursos"], ["contact", "/contacto"],
];

const LangSwitch = () => {
  const { lang, setLang } = useT();
  return (
    <button data-testid="lang-switch" onClick={() => setLang(lang === "es" ? "en" : "es")} aria-label="Cambiar idioma"
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-xs font-mono font-semibold text-slate-600 hover:bg-slate-100 transition-colors">
      <Globe className="h-3.5 w-3.5" /> {lang.toUpperCase()}
    </button>
  );
};

const Actions = () => {
  const { t } = useT();
  const { count } = useCart();
  const { user } = useAuth();
  const { site } = useSite();
  return (
    <>
      {site.cart_enabled && <Link to="/carrito" data-testid="header-cart-link" aria-label={t("cart")} className="relative rounded-full p-2 text-slate-700 hover:bg-slate-100 transition-colors">
        <ShoppingCart className="h-5 w-5" />
        {count > 0 && <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-brand px-1 text-[10px] font-bold text-white">{count}</span>}
      </Link>}
      <Link to={user ? (isStaff(user) ? "/admin" : "/portal") : "/acceso"} data-testid="header-portal-link"
        className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors">
        <User className="h-4 w-4" /> {user ? (isStaff(user) ? "Admin" : "Mi cuenta") : t("portal")}
      </Link>
    </>
  );
};

export function Header() {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const linkCls = ({ isActive }) =>
    `px-3 py-2 text-[13px] font-semibold tracking-wide transition-colors ${isActive ? "text-brand" : "text-slate-600 hover:text-slate-900"}`;
  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-md" data-testid="site-header">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-2 bg-white px-3 py-2 rounded">Saltar al contenido</a>
      <div className="wrap flex h-16 items-center justify-between gap-4">
        <Logo />
        <nav className="hidden xl:flex items-center" aria-label="Principal">
          {NAV.map(([k, to]) => (
            <NavLink key={k} to={to} end={to === "/"} className={linkCls} data-testid={`nav-${k}`}>{t(k)}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-1">
          <LangSwitch />
          <div className="hidden sm:flex items-center gap-1"><Actions /></div>
          <Link to="/demo" data-testid="header-demo-btn" className="btn-cc hidden md:inline-flex !py-2.5 ml-2">{t("demo")}</Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="xl:hidden rounded-full p-2 hover:bg-slate-100" aria-label="Abrir menú" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80">
              <SheetTitle className="sr-only">Menú</SheetTitle>
              <nav className="mt-8 flex flex-col" aria-label="Móvil">
                {NAV.map(([k, to]) => (
                  <NavLink key={k} to={to} end={to === "/"} onClick={() => setOpen(false)} data-testid={`mobile-nav-${k}`}
                    className={({ isActive }) => `border-b border-slate-100 py-3 text-sm font-semibold ${isActive ? "text-brand" : "text-slate-700"}`}>{t(k)}</NavLink>
                ))}
              </nav>
              <div className="mt-6 flex items-center gap-2 sm:hidden" onClick={() => setOpen(false)}><Actions /></div>
              <Link to="/demo" onClick={() => setOpen(false)} className="btn-cc mt-6 w-full" data-testid="mobile-demo-btn">{t("demo")}</Link>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
